import React from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  AdminHubPage,
  AdminIntegrationsPage,
  AdminLifecyclePage,
  AdminNumbersPage,
  AdminProvidersPage,
  AdminReconciliationPage,
  AdminSubscriptionsPage,
  AdminWebhooksPage,
  DefineIntegrationPage
} from "./pages/AdminPages";
import { CallEndedNotice, CallOverlay } from "./pages/CallOverlay";
import { CallListPage, ConversationsPage, TimelinePage } from "./pages/CommunicationPages";
import { DemoPanel } from "./pages/DemoPanel";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { LoginPage } from "./pages/LoginPage";
import { BuyNumberPage, NumberDetailPage, NumbersPage } from "./pages/NumberPages";
import { PaymentMethodPage, PaymentsPage } from "./pages/PaymentPages";
import { AddContactPage, BlockListPage, BlockNumberPage, ContactsPage } from "./pages/PeoplePages";
import { PromoteAdminPage } from "./pages/PromoteAdminPage";
import { ThreadPage } from "./pages/ThreadPage";
import { useApp } from "./store";
import { BottomNav, Loading } from "./ui";

/**
 * The app shell: the phone frame from the design drafts, a call overlay that can appear over anything, and
 * the five-tab navigation. Desktop layout is deferred in the design, so the mobile shape is kept.
 */
export function App(): JSX.Element {
  const { user, loading } = useApp();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="phone">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="phone">
        <LoginPage />
      </div>
    );
  }

  const showNav = !pathname.startsWith("/conversations/");

  return (
    <div className="phone">
      <CallOverlay />
      <Routes>
        <Route path="/" element={<Navigate to="/timeline" replace />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/calls" element={<CallListPage />} />
        <Route path="/conversations" element={<ConversationsPage />} />
        <Route path="/conversations/:conversationId" element={<ThreadPage />} />
        <Route path="/numbers" element={<NumbersPage />} />
        <Route path="/numbers/buy" element={<BuyNumberPage />} />
        <Route path="/numbers/:numberId" element={<NumberDetailPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/payments/method" element={<PaymentMethodPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/new" element={<AddContactPage />} />
        <Route path="/blocks" element={<BlockListPage />} />
        <Route path="/blocks/new" element={<BlockNumberPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/admin" element={<AdminGuard><AdminHubPage /></AdminGuard>} />
        <Route path="/admin/integrations" element={<AdminGuard><AdminIntegrationsPage /></AdminGuard>} />
        <Route
          path="/admin/integrations/new"
          element={
            <AdminGuard>
              <DefineIntegrationPage />
            </AdminGuard>
          }
        />
        <Route path="/admin/providers" element={<AdminGuard><AdminProvidersPage /></AdminGuard>} />
        <Route path="/admin/reconciliation" element={<AdminGuard><AdminReconciliationPage /></AdminGuard>} />
        <Route path="/admin/lifecycle" element={<AdminGuard><AdminLifecyclePage /></AdminGuard>} />
        <Route path="/admin/webhooks" element={<AdminGuard><AdminWebhooksPage /></AdminGuard>} />
        <Route path="/admin/subscriptions" element={<AdminGuard><AdminSubscriptionsPage /></AdminGuard>} />
        <Route path="/admin/numbers" element={<AdminGuard><AdminNumbersPage /></AdminGuard>} />
        <Route path="/menu" element={<MenuPage />} />
        {/* Deliberately not linked from anywhere: you reach it by typing the URL. The backend refuses
            anyone whose address is not the configured one, so this is not the control, only discretion. */}
        <Route path="/menu/admin-access" element={<PromoteAdminPage />} />
        <Route path="*" element={<Navigate to="/timeline" replace />} />
      </Routes>
      <CallEndedNotice />
      <DemoPanel />
      {showNav ? <BottomNav /> : null}
    </div>
  );
}

/** Admin pages are gated client-side for navigation; the backend enforces the role. */
function AdminGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const { user } = useApp();
  if (user?.role !== "ADMIN") {
    return <Navigate to="/timeline" replace />;
  }
  return <>{children}</>;
}

/** The remaining destinations that are not in the bottom bar. */
function MenuPage(): JSX.Element {
  const { user, logout } = useApp();
  return (
    <>
      <header className="app-header">
        <h1>Menu</h1>
        <button className="link" type="button" onClick={() => logout()}>
          Sign out
        </button>
      </header>
      <div className="content">
        <div className="section-label">Account</div>
        <ul className="list">
          <li>
            <div className="row-main">
              <div className="row-title">{user?.displayName}</div>
              <div className="row-sub">
                {user?.email} {"\u00B7"} {user?.role.toLowerCase()}
              </div>
            </div>
          </li>
          <li>
            <div className="row-main">
              <div className="row-title">
                <Link className="back" to="/payments">
                  Payments
                </Link>
              </div>
            </div>
          </li>
          <li>
            <div className="row-main">
              <div className="row-title">
                <Link className="back" to="/integrations">
                  Integration settings
                </Link>
              </div>
            </div>
          </li>
          <li>
            <div className="row-main">
              <div className="row-title">
                <Link className="back" to="/blocks">
                  Blocked numbers
                </Link>
              </div>
            </div>
          </li>
          {user?.role === "ADMIN" ? (
            <li>
              <div className="row-main">
                <div className="row-title">
                  <Link className="back" to="/admin">
                    Admin
                  </Link>
                </div>
              </div>
            </li>
          ) : null}
        </ul>
      </div>
    </>
  );
}
